import React, { useCallback, useMemo } from 'react'
import Checkbox from '@material-ui/core/Checkbox'
import CheckBoxIcon from '@material-ui/icons/CheckBox'
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank'
import { FormHelperText, MenuItem } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import { Field, useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'
import { SUPPORTED_LOCALES } from 'civic-champs-translations/src'
import { StyledOutlinedTextField } from 'civic-champs-shared/formik/components'
import { adminLocaleLabelKey } from 'i18n/adminLocales'

type LocaleOption = { id: string; name: string }

const useStyles = makeStyles({
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  subHeader: {
    fontFamily: 'Nunito',
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
    letterSpacing: '0.5px',
    textAlign: 'left',
    margin: 0,
  },
  localeList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  requiredLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0,
    cursor: 'pointer',
    fontFamily: 'Nunito, sans-serif',
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '20px',
    letterSpacing: '0.25px',
    color: '#000000',
  },
  checkboxRoot: {
    padding: '6px 8px',
    color: '#74777F',
    '&$checkboxChecked': {
      color: '#0F5DB5',
    },
  },
  checkboxChecked: {},
  checkboxIcon: {
    fontSize: '20px',
  },
})

export default function OrganizationAdminLocalesFields() {
  const classes = useStyles()
  const { t, i18n } = useTranslation(['admin', 'common'])
  const { values, setFieldValue, touched, errors } = useFormikContext<{
    supportedAdminLocales?: string[]
    defaultAdminLocale?: string
  }>()

  const supported = values.supportedAdminLocales?.length ? values.supportedAdminLocales : ['en']

  const toggleLocale = useCallback(
    (locale: string, checked: boolean) => {
      let next = checked ? [...new Set([...supported, locale])] : supported.filter(code => code !== locale)
      if (!next.length) {
        next = ['en']
      }
      setFieldValue('supportedAdminLocales', next)
      if (!next.includes(values.defaultAdminLocale || '')) {
        setFieldValue('defaultAdminLocale', next[0])
      }
    },
    [setFieldValue, supported, values.defaultAdminLocale],
  )

  const allLocaleOptions: LocaleOption[] = useMemo(
    () =>
      SUPPORTED_LOCALES.map(code => ({
        id: code,
        name: t(adminLocaleLabelKey(code), { ns: 'common' }),
      })),
    [t, i18n.language],
  )

  const defaultLocaleOptions = useMemo(
    () => allLocaleOptions.filter(option => supported.includes(option.id)),
    [allLocaleOptions, supported],
  )

  return (
    <div className={classes.section}>
      <div className={classes.subHeader}>{t('admin:organization.adminLanguages')}</div>
      <div className={classes.localeList}>
        {allLocaleOptions.map(({ id, name }) => (
          <label key={id} className={classes.requiredLabel}>
            <Checkbox
              color="primary"
              classes={{
                root: classes.checkboxRoot,
                checked: classes.checkboxChecked,
              }}
              icon={<CheckBoxOutlineBlankIcon className={classes.checkboxIcon} />}
              checkedIcon={<CheckBoxIcon className={classes.checkboxIcon} />}
              checked={supported.includes(id)}
              onChange={event => toggleLocale(id, event.target.checked)}
            />
            {name}
          </label>
        ))}
      </div>
      {touched.supportedAdminLocales && errors.supportedAdminLocales && (
        <FormHelperText error>{errors.supportedAdminLocales as string}</FormHelperText>
      )}
      <Field
        name="defaultAdminLocale"
        component={StyledOutlinedTextField}
        select
        marginless
        id="defaultAdminLocale"
        label={t('admin:organization.defaultAdminLocale')}
        variant="outlined"
        fullWidth
      >
        {defaultLocaleOptions.map(option => (
          <MenuItem key={option.id} value={option.id}>
            {option.name}
          </MenuItem>
        ))}
      </Field>
    </div>
  )
}
